// Sopwith 3D - sound.
//
// Everything is synthesised with the Web Audio API, in the spirit of the
// original's PC speaker: an engine drone whose pitch follows the throttle,
// gun clicks, a falling-bomb whistle, explosion rumbles and a windscreen
// crack. Nothing is sampled, so there are no assets to load. The context is
// created on the first user gesture, as browsers require.

import type { Game } from '../sim/game';
import { Obj, ObjState, ObjType } from '../sim/types';
import type { TuneNote } from './tune';

export class Audio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private engine!: OscillatorNode;
  private engineGain!: GainNode;
  private engineFilter!: BiquadFilterNode;
  private whistle!: OscillatorNode;
  private whistleGain!: GainNode;
  private noise!: AudioBuffer;
  private seen = new WeakSet<Obj>();
  private lastExplosion = 0;
  private tuneOsc: OscillatorNode | null = null;
  private tuneGain: GainNode | null = null;
  private tuneTimer = 0;
  private tuneNextStart = 0;
  enabled = true;

  /** Create the context; call from a click or key handler. */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.enabled ? 0.5 : 0;
    this.master.connect(ctx.destination);

    // Engine: two detuned saws through a low-pass, gated by speed.
    this.engineFilter = ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 420;
    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0;
    this.engine = ctx.createOscillator();
    this.engine.type = 'sawtooth';
    this.engine.frequency.value = 60;
    const engine2 = ctx.createOscillator();
    engine2.type = 'square';
    engine2.frequency.value = 30;
    const e2gain = ctx.createGain();
    e2gain.gain.value = 0.35;
    this.engine.connect(this.engineFilter);
    engine2.connect(e2gain).connect(this.engineFilter);
    this.engineFilter.connect(this.engineGain).connect(this.master);
    this.engine.start();
    engine2.start();
    // Keep the second oscillator an octave below the first.
    const track = (): void => {
      engine2.frequency.value = this.engine.frequency.value / 2;
      requestAnimationFrame(track);
    };
    track();

    // Bomb whistle.
    this.whistle = ctx.createOscillator();
    this.whistle.type = 'sine';
    this.whistle.frequency.value = 900;
    this.whistleGain = ctx.createGain();
    this.whistleGain.gain.value = 0;
    this.whistle.connect(this.whistleGain).connect(this.master);
    this.whistle.start();

    // Two seconds of white noise for percussive sounds.
    const len = ctx.sampleRate * 2;
    this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  }

  /** Play a title tune on a square wave, looping until stopTune(). */
  playTune(notes: TuneNote[]): void {
    if (!this.ctx || this.tuneOsc || !notes.length) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    const gain = ctx.createGain();
    gain.gain.value = 0;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 2400;
    osc.connect(filter).connect(gain).connect(this.master);
    osc.start();
    this.tuneOsc = osc;
    this.tuneGain = gain;
    this.tuneNextStart = 0;

    // Schedule a loop's worth of notes ahead, then re-arm before it ends.
    const schedule = (): void => {
      if (this.tuneOsc !== osc) return;
      let t = Math.max(ctx.currentTime + 0.05, this.tuneNextStart);
      for (const n of notes) {
        const dur = n.ticks / 10;
        if (n.freq > 0) {
          osc.frequency.setValueAtTime(n.freq, t);
          gain.gain.setValueAtTime(0.11, t);
          gain.gain.setValueAtTime(0, t + dur - 0.03);
        } else {
          gain.gain.setValueAtTime(0, t);
        }
        t += dur;
      }
      this.tuneNextStart = t;
      this.tuneTimer = window.setTimeout(schedule, (t - ctx.currentTime - 0.5) * 1000);
    };
    schedule();
  }

  stopTune(): void {
    if (this.tuneTimer) {
      clearTimeout(this.tuneTimer);
      this.tuneTimer = 0;
    }
    if (this.tuneOsc && this.ctx) {
      const osc = this.tuneOsc;
      const gain = this.tuneGain!;
      gain.gain.cancelScheduledValues(this.ctx.currentTime);
      gain.gain.setValueAtTime(0, this.ctx.currentTime);
      osc.stop(this.ctx.currentTime + 0.05);
      this.tuneOsc = null;
      this.tuneGain = null;
    }
  }

  get tunePlaying(): boolean {
    return this.tuneOsc !== null;
  }

  get ready(): boolean {
    return this.ctx !== null;
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    if (this.master) {
      this.master.gain.setTargetAtTime(this.enabled ? 0.5 : 0, this.ctx!.currentTime, 0.02);
    }
    return this.enabled;
  }

  private burst(
    duration: number,
    gain: number,
    filterType: BiquadFilterType,
    freq: number,
    q = 1,
    freqEnd?: number,
  ): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = filterType;
    f.frequency.value = freq;
    f.Q.value = q;
    if (freqEnd !== undefined) {
      f.frequency.exponentialRampToValueAtTime(freqEnd, ctx.currentTime + duration);
    }
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    src.connect(f).connect(g).connect(this.master);
    src.start();
    src.stop(ctx.currentTime + duration);
  }

  gun(): void {
    this.burst(0.06, 0.5, 'bandpass', 1800, 2);
  }

  explosion(big: boolean): void {
    this.burst(big ? 1.1 : 0.5, big ? 1.2 : 0.7, 'lowpass', big ? 900 : 600, 0.7, 80);
  }

  crack(): void {
    this.burst(0.12, 0.6, 'highpass', 2500, 1);
  }

  thud(): void {
    this.burst(0.3, 0.8, 'lowpass', 200, 1, 40);
  }

  /** Called once per rendered frame with the current game state. */
  update(g: Game): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    const p = g.consoleplayer;

    // Engine follows the player's speed and throttle.
    const running = p.speed > 0 && p.state !== ObjState.CRASHED && p.state !== ObjState.FALLING;
    const target = running ? 0.35 : 0;
    this.engineGain.gain.setTargetAtTime(target, now, 0.08);
    const freq = 45 + p.speed * 9 + p.accel * 4;
    this.engine.frequency.setTargetAtTime(freq, now, 0.15);
    this.engineFilter.frequency.setTargetAtTime(300 + p.speed * 40, now, 0.2);

    // Scan for new objects worth a sound.
    let newShots = 0;
    let newExplosions = 0;
    let bigExplosion = false;
    let bomb: Obj | null = null;
    for (let ob = g.world.objtop; ob; ob = ob.next) {
      if (ob.type === ObjType.BOMB && ob.owner === p) {
        bomb = ob;
      }
      if (this.seen.has(ob)) continue;
      this.seen.add(ob);
      if (ob.type === ObjType.SHOT) {
        newShots++;
      } else if (ob.type === ObjType.EXPLOSION) {
        newExplosions++;
        if (ob.owner && ob.owner.type === ObjType.TARGET) bigExplosion = true;
      }
    }
    if (newShots > 0) this.gun();
    if (newExplosions > 0 && now - this.lastExplosion > 0.12) {
      this.lastExplosion = now;
      this.explosion(bigExplosion || newExplosions > 10);
    }

    // Bomb whistle drops in pitch as the bomb gains speed.
    if (bomb) {
      this.whistleGain.gain.setTargetAtTime(0.12, now, 0.05);
      this.whistle.frequency.setTargetAtTime(1100 + bomb.dy * 70, now, 0.05);
    } else {
      this.whistleGain.gain.setTargetAtTime(0, now, 0.05);
    }

    for (const ev of g.collisionEvents) {
      if (ev.kind === 'windshot') this.crack();
      else this.thud();
    }
  }
}
